/* ==========================================================================
   Buena Vita — header behaviour

   Sticky bar that hides on scroll down and returns on scroll up, a mega menu,
   a search panel, and a mobile drawer.

   Scroll handling reads scrollY inside a requestAnimationFrame rather than in
   the scroll event itself. Reading layout in the event handler forces a sync
   reflow on every tick, which is the classic way a sticky header becomes the
   jankiest thing on a page.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bvHeaderInit) return;
  window.__bvHeaderInit = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function boot() {
    var header = document.querySelector('[data-bv-header]');
    if (!header) return;

    /* ------------------------------------------------------------------
       Sticky show/hide
       ------------------------------------------------------------------ */

    var lastY = window.scrollY || 0;
    var ticking = false;

    /* "The Day" runs as a pinned, full-height stage. A bar floating over it
       covers the top of the sky and breaks the illusion, so the header parks
       for the length of that section and resumes its normal hide-on-down /
       reveal-on-up behaviour once the section is past.

       Gated on .is-live because bv-ritual falls back to a plain stacked
       layout when it cannot fit the viewport, and a short static section has
       no business taking the nav away. */
    var ritual = document.querySelector('.bv-ritual');

    function onFrame() {
      var y = window.scrollY || 0;

      header.classList.toggle('is-stuck', y > 8);

      /* Never hide while a panel is open — the shopper is interacting with
         something inside the bar, and yanking it away mid-gesture is hostile.
         Also stay put near the top, where hiding just looks like a glitch. */
      var panelOpen =
        header.querySelector('.bv-hd__mega.is-open') ||
        header.querySelector('.bv-hd__search.is-open');

      /* Reading the rect here rather than in the scroll handler is the whole
         point of the rAF: layout has already settled for this frame, so it
         costs nothing extra. True exactly while the section spans the top of
         the viewport; the 1px slack keeps a sub-pixel rect from flickering
         the bar at the seam. */
      var parked = false;
      if (ritual && ritual.classList.contains('is-live')) {
        var r = ritual.getBoundingClientRect();
        parked = r.top <= 1 && r.bottom > 1;
      }
      header.classList.toggle('is-parked', parked);

      if (parked && !panelOpen) {
        header.classList.add('is-hidden');
      } else if (!reduced && !panelOpen && y > 140) {
        header.classList.toggle('is-hidden', y > lastY + 4);
      } else {
        header.classList.remove('is-hidden');
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(onFrame);
      },
      { passive: true }
    );

    onFrame();

    /* ------------------------------------------------------------------
       Mega menu
       ------------------------------------------------------------------ */

    var megaBtn = header.querySelector('[data-bv-mega-toggle]');
    var mega = header.querySelector('[data-bv-mega]');

    function closeMega() {
      if (!mega) return;
      mega.classList.remove('is-open');
      megaBtn.setAttribute('aria-expanded', 'false');
    }

    if (megaBtn && mega) {
      megaBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = mega.classList.toggle('is-open');
        megaBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) closeSearch();
      });

      /* Hover opens it on pointer devices, which is what people expect from a
         shop menu — but only where hover is real, so a tap on a touch screen
         does not both open and immediately re-close it. */
      if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        var hoverTimer = null;
        var wrap = megaBtn.parentElement;

        wrap.addEventListener('mouseenter', function () {
          clearTimeout(hoverTimer);
          mega.classList.add('is-open');
          megaBtn.setAttribute('aria-expanded', 'true');
        });

        wrap.addEventListener('mouseleave', function () {
          hoverTimer = setTimeout(closeMega, 180);
        });

        mega.addEventListener('mouseenter', function () { clearTimeout(hoverTimer); });
        mega.addEventListener('mouseleave', function () { hoverTimer = setTimeout(closeMega, 180); });
      }

      document.addEventListener('click', function (e) {
        if (!mega.contains(e.target) && e.target !== megaBtn) closeMega();
      });
    }

    /* ------------------------------------------------------------------
       Search
       ------------------------------------------------------------------ */

    var searchBtn = header.querySelector('[data-bv-search-toggle]');
    var search = header.querySelector('[data-bv-search]');
    var searchInput = header.querySelector('[data-bv-search-input]');

    function closeSearch() {
      if (!search) return;
      search.classList.remove('is-open');
      if (searchBtn) searchBtn.setAttribute('aria-expanded', 'false');
    }

    if (searchBtn && search) {
      searchBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = search.classList.toggle('is-open');
        searchBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          closeMega();
          if (searchInput) searchInput.focus();
        }
      });
    }

    /* ------------------------------------------------------------------
       Mobile drawer
       ------------------------------------------------------------------ */

    var openBtn = header.querySelector('[data-bv-hd-open]');
    var drawer = document.querySelector('[data-bv-hd-drawer]');
    var scrim = document.querySelector('[data-bv-hd-scrim]');
    var closeBtn = drawer ? drawer.querySelector('[data-bv-hd-close]') : null;
    var lastFocus = null;

    function openDrawer() {
      if (!drawer) return;
      lastFocus = document.activeElement;

      drawer.hidden = false;
      if (scrim) scrim.hidden = false;

      /* Reflow so the transition runs from the hidden state rather than being
         batched with the hidden-attribute removal and skipped. */
      void drawer.offsetWidth;

      drawer.classList.add('is-open');
      if (scrim) scrim.classList.add('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');

      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function closeDrawer() {
      if (!drawer || drawer.hidden) return;

      drawer.classList.remove('is-open');
      if (scrim) scrim.classList.remove('is-open');
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';

      /* Wait for the slide-out before hiding, otherwise it vanishes instantly.
         hidden matters here: a drawer left in the DOM but off-screen keeps its
         links in the tab order. */
      setTimeout(function () {
        drawer.hidden = true;
        if (scrim) scrim.hidden = true;
      }, reduced ? 0 : 340);

      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    if (openBtn) openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (scrim) scrim.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeDrawer();
      closeMega();
      closeSearch();
    });

    /* Anything that navigates should leave the drawer shut behind it. */
    if (drawer) {
      drawer.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('a')) closeDrawer();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    window.__bvHeaderInit = false;
    boot();
    window.__bvHeaderInit = true;
  });
})();
